import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db7lhon3hiaolq192iu0";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    const honey = String(fd.get("contact_alt") ?? "").trim();
    if (honey) {
      setDone(true);
      setStatus("success");
      formEl.reset();
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "contact_alt") return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    if (!params.get("email") || !params.get("message")) {
      setStatus("error");
      setFormError("请填写邮箱和反馈内容后再提交。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";

      const failed =
        !response.ok ||
        parsed?.code !== "OK" ||
        /spam|form data is spam/i.test(serverMsg);

      if (failed) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setDone(true);
      formEl.reset();
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={onSubmit}
      className="rounded-lg border border-background-800 bg-background-900 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-50">意见反馈</h3>
      <p className="mt-2 text-sm text-foreground-400">
        对我们的内容、分类或功能有任何建议，欢迎通过下方表单告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-300" htmlFor="fb-nickname">
            昵称
          </label>
          <input
            id="fb-nickname"
            name="nickname"
            type="text"
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-300" htmlFor="fb-email">
            邮箱 <span className="text-primary-400">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-foreground-300" htmlFor="fb-type">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="feedback_type"
          defaultValue="content"
          className="w-full rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors focus:border-primary-500"
        >
          <option value="content">内容建议</option>
          <option value="category">分类与筛选</option>
          <option value="bug">功能问题</option>
          <option value="cooperation">合作与投稿</option>
        </select>
      </div>

      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-foreground-300" htmlFor="fb-message">
          反馈内容 <span className="text-primary-400">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的建议或遇到的问题（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
        />
      </div>

      <div className="aux-field-91x" aria-hidden="true">
        <label htmlFor="fb-alt">请勿填写此栏</label>
        <input
          id="fb-alt"
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-bold text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg" /> 提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-line text-lg" /> 提交反馈
            </>
          )}
        </button>
        {status === "success" && done && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-secondary-300">
            <i className="ri-checkbox-circle-line" /> 反馈已提交，感谢你的建议！
          </span>
        )}
      </div>

      {status === "error" && formError && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-400">
          <i className="ri-error-warning-line" /> {formError}
        </p>
      )}
    </form>
  );
}